import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useMenu } from '../../hooks/useMenu';
import { secureAddressSecondOwnerTitle } from '../../util/helpers/onboardingContentHelpers';
import useApplicationOwnerName from '../../hooks/useApplicationOwnerName';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  SAME_ADDRESS,
} from '../../constants/sessionStorage';
import useApplicationOwnerAddress from '../../hooks/useApplicationOwnerAddress';
import { APPLICANT_TYPES } from '../../util/constants';
import useStepPost from '../../hooks/useStepPost';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { HeadingSubtitle } from '../../components/Heading';
import SelectList from '../../components/SelectList/SelectList';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const SecureAddressSecondOwner = ({ applicantType }) => {
  const { activeMenu, activeItem } = useMenu();
  const { t } = useTranslation();
  const applicantName = useApplicationOwnerName(applicantType);
  const applicantAddress = useApplicationOwnerAddress(APPLICANT_TYPES.MAIN);
  const ApplicantData = useApplicantData(applicantType);
  const { onSuccess } = useStepPost({ nextPath: activeItem?.nextPath });
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const { BrokerName } = useSelector(selectSettings);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (ApplicantData?.PersonName && ApplicantData?.PersonBirthday) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Address Second Owner',
        accountType: accountGtmType,
        accountName: ApplicantData?.PersonName,
        accountDateOfBirth: ApplicantData?.PersonBirthday,
        accountPhone: ApplicantData?.Phone,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = [
    {
      title: t('common.yes'),
      onClick: () => {
        storeInSessionStorage(SAME_ADDRESS, true);
        onSuccess();
      },
    },
    {
      title: t('common.no'),
      onClick: () => {
        storeInSessionStorage(SAME_ADDRESS, false);
        onSuccess();
      },
      showDivider: false,
    },
  ];

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('titleTagSecureAccount', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {secureAddressSecondOwnerTitle(
          applicantType,
          { name: applicantName },
          { address: applicantAddress },
          isSalesAssisted,
        )}
      </HeadingSubtitle>

      <SelectList options={items} />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

SecureAddressSecondOwner.propTypes = {
  applicantType: PropTypes.string,
};

export default SecureAddressSecondOwner;
